import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {
  convertFromInternalFilterInterface,
  convertToInternalFilterInterface,
} from 'data-frames/filters/components/convertFilterInterface';
import {
  DataFrameFilter,
  DataFrameFilterOperation,
  UserFacingFilter,
} from 'data-frames/filters/DataFrameFilterTypes';
import {getColumnOptionDatas} from 'insights/analytics-visualizations/configs/config-components/ConfigEditorUtils';
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {DataFrameFilterOptions} from '../DataFrameFilterOptions';
import InternalDataFrameFilterCreator from './InternalDataFrameFilterCreator';
import isFilterValid from './isFilterValid';

export type FilterOptionsForCreator = Pick<
  DataFrameFilterOptions,
  'getFilterForColumnHeaders' | 'updateFilterForColumn'
>;

export interface DataFrameFilterCreatorProps {
  canSelectColumn?: boolean;
  columns: Array<DataFrameColumnDef>;
  // Specify our own filter options, because different callers will pass in different getters/setters
  filterOptionsForCreator: FilterOptionsForCreator;
  onCancel?: () => void;
  onSave?: () => void;
  initialSelectedColumnName: string;
}

/**
 * Create and edit a multi-condition filter.
 *
 * This component translates between the outward-facing interface persisted by the FILTER_DATASET step and
 * the internal-facing interface that follows how filters are presented to the user.
 */
export default function DataFrameFilterCreator({
  canSelectColumn,
  columns,
  filterOptionsForCreator,
  onCancel,
  onSave,
  initialSelectedColumnName,
}: DataFrameFilterCreatorProps): JSX.Element {
  const [selectedColumnSystemName, setSelectedColumnSystemName] = useState<string>(initialSelectedColumnName);

  function makeDefaultFilter(columnSystemName: string): DataFrameFilter {
    return {
      operation: DataFrameFilterOperation.AND,
      filters: [
        {
          operation: DataFrameFilterOperation.IS_NULL,
          columnSystemName,
          not_: true,
        },
      ],
    };
  }

  const [cachedFilter, setCachedFilter] = useState<DataFrameFilter>(
    filterOptionsForCreator.getFilterForColumnHeaders(selectedColumnSystemName) ??
      makeDefaultFilter(selectedColumnSystemName)
  );

  const handleUpdateFilters = (
    conjunction: DataFrameFilterOperation.AND | DataFrameFilterOperation.OR,
    newFilters: Array<UserFacingFilter>
  ): void => {
    setCachedFilter({
      operation: conjunction,
      filters: newFilters.map((newFilter) => convertFromInternalFilterInterface(newFilter)),
    });
  };
  const handleSave = (): void => {
    if (cachedFilter.filters.length === 0) {
      filterOptionsForCreator.updateFilterForColumn(selectedColumnSystemName, null);
    } else {
      filterOptionsForCreator.updateFilterForColumn(selectedColumnSystemName, cachedFilter);
    }
    onSave?.();
  };

  const handleCancel = (): void => {
    setCachedFilter(
      filterOptionsForCreator.getFilterForColumnHeaders(selectedColumnSystemName) ??
        makeDefaultFilter(selectedColumnSystemName)
    );
    onCancel?.();
  };

  return (
    <div {...getTestProps({component: 'DataFrameFilterCreator', element: 'root'})}>
      {canSelectColumn && (
        <div className={css(styles.columnSelector)}>
          <FieldLabel label="Column to filter" />
          <DropdownSelector
            allowNull={false}
            onChange={(columnSystemName: string) => {
              setSelectedColumnSystemName(columnSystemName);
              setCachedFilter(
                filterOptionsForCreator.getFilterForColumnHeaders(columnSystemName) ??
                  makeDefaultFilter(columnSystemName)
              );
            }}
            searchable={true}
            selectedValue={selectedColumnSystemName}
            optionDatas={getColumnOptionDatas(
              columns.filter(
                (column) =>
                  initialSelectedColumnName === column.systemName ||
                  filterOptionsForCreator.getFilterForColumnHeaders(column.systemName) == null
              ),
              true
            )}
          />
        </div>
      )}
      <InternalDataFrameFilterCreator
        column={columns.find((column) => column.systemName === selectedColumnSystemName)!}
        conjunction={cachedFilter.operation}
        filters={cachedFilter.filters.map((condition) => convertToInternalFilterInterface(condition))}
        onFiltersChange={handleUpdateFilters}
      />
      <div className={css(styles.buttonContainer)}>
        <div className={css(styles.button)}>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            text="Apply"
            onClick={handleSave}
            disabled={!isFilterValid(cachedFilter, columns)}
            dataTestAttrs={{component: 'DataFrameFilterCreator', element: 'applyButton'}}
          />
        </div>
        <div className={css(styles.button)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            text="Cancel"
            onClick={handleCancel}
          />
        </div>
      </div>
    </div>
  );
}

export const styles = StyleSheet.create({
  button: {
    marginLeft: 5,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: 5,
  },
  columnSelector: {
    marginBottom: 15,
  },
});
